import { useQuickUpload, QUICK_UPLOAD_ACCEPT_ATTR } from '@/hooks/useQuickUpload';
import { cn } from '@/lib/utils';
import { CloudUpload } from '@mynaui/icons-react';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface iQuickUploadDropZoneProps {
  projectId: string;
}

/**
 * Discoverability button for anonymous shared-link viewers (logged-in users
 * have the in-panel "Add temporary file" button). Renders nothing for non-share
 * contexts. Designed to sit alongside the viewer's toolbar buttons.
 */
export const QuickUploadSharedLinkCta: FunctionComponent<iQuickUploadDropZoneProps> = ({ projectId }) => {
  const { t } = useTranslation('project');
  const { handleFiles, isAllowed, isSharedLink } = useQuickUpload(projectId);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!isAllowed || !isSharedLink) return null;

  return (
    <>
      <input
        ref={inputRef}
        type='file'
        accept={QUICK_UPLOAD_ACCEPT_ATTR}
        multiple
        className='hidden'
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <button
        type='button'
        onClick={() => inputRef.current?.click()}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-md',
          'bg-white/80 dark:bg-stone-900/80 backdrop-blur-md border border-slate-200/70 dark:border-stone-700/70',
          'text-xs text-slate-700 dark:text-stone-200 hover:bg-white dark:hover:bg-stone-900 transition-colors',
        )}>
        <CloudUpload className='size-4 text-blue-600 dark:text-blue-400' />
        {t('quickUpload.sharedLinkCta')}
      </button>
    </>
  );
};

/**
 * Window-wide drag-and-drop overlay. All upload logic lives in useQuickUpload
 * — this component is purely UI.
 */
export const QuickUploadDropZone: FunctionComponent<iQuickUploadDropZoneProps> = ({ projectId }) => {
  const { t } = useTranslation('project');
  const { handleFiles, isAllowed, isPending } = useQuickUpload(projectId);

  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  useEffect(() => {
    if (!isAllowed) return;
    const onEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      dragCounter.current += 1;
      setIsDragging(true);
    };
    const onOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
    };
    const onLeave = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setIsDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes('Files')) return;
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files && files.length > 0) handleFiles(files);
    };

    window.addEventListener('dragenter', onEnter);
    window.addEventListener('dragover', onOver);
    window.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onEnter);
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [isAllowed, handleFiles]);

  if (!isAllowed) return null;
  if (!isDragging && !isPending) return null;

  return (
    <div
      className={cn(
        'absolute inset-0 z-50 flex items-center justify-center pointer-events-none',
        'bg-blue-500/10 dark:bg-blue-400/10 backdrop-blur-sm',
        'border-4 border-dashed border-blue-500 dark:border-blue-400',
      )}>
      <div className='flex flex-col items-center gap-3 px-6 py-4 rounded-xl bg-white/90 dark:bg-stone-900/90 shadow-xl'>
        <CloudUpload className='size-10 text-blue-600 dark:text-blue-400' />
        <div className='text-center'>
          <p className='font-semibold text-slate-900 dark:text-white'>
            {isPending ? t('quickUpload.uploading') : t('quickUpload.dropPrompt')}
          </p>
          <p className='text-xs text-slate-600 dark:text-stone-300 mt-1'>
            {t('quickUpload.acceptedFormats')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickUploadDropZone;
